import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  LayoutDashboard, FileText, Brain, Target, Leaf, Activity, 
  TrendingUp, ListTodo, Users, Map, BarChart3, Shield, 
  Zap, Database, Cloud, Server, ArrowRight, CheckCircle,
  Globe, Lock, RefreshCw
} from 'lucide-react';

export default function About() {
  const featureCategories = [
    {
      title: 'Dashboard & Analytics',
      icon: LayoutDashboard,
      color: 'bg-blue-100 text-blue-700',
      features: [
        'Executive Dashboard with portfolio KPIs and metrics',
        'Expiring contracts timeline (30/60/90 days)',
        'Interactive geospatial visualization with contract locations',
        'Territory analysis with ISO region overlays',
        'Split-screen map comparison views',
        'Time-based animation for contract hotspots',
        'What-if scenario modeling for rate changes'
      ]
    },
    {
      title: 'Contract Management',
      icon: FileText,
      color: 'bg-green-100 text-green-700',
      features: [
        'Comprehensive contract listing with advanced filtering',
        'Contract detail views with full history',
        'AI-powered rate competitiveness scoring',
        'Days-to-expiration tracking and alerts',
        'Contract status workflow management',
        'Assigned user tracking and management',
        'Bulk contract filtering by expiration window'
      ]
    },
    {
      title: 'Predictive Analytics',
      icon: Brain,
      color: 'bg-purple-100 text-purple-700',
      features: [
        'AI-powered renewal probability predictions',
        'Multi-factor scoring (competitiveness, tier, value, regional patterns)',
        'Risk categorization (At Risk, Uncertain, Likely to Renew)',
        'Actionable recommendations for each contract',
        'High-value at-risk contract alerts',
        'Prediction confidence intervals',
        'Historical trend analysis with sparklines'
      ]
    },
    {
      title: 'Competitive Intelligence',
      icon: Target,
      color: 'bg-orange-100 text-orange-700',
      features: [
        'Competitor profile management',
        'Win/Loss recording and analysis',
        'Win rate tracking by competitor and region',
        'Loss reason analytics (Price, Service, Relationship)',
        'Market intelligence feed with impact levels',
        'Competitive landscape visualization',
        'Rate comparison benchmarking'
      ]
    },
    {
      title: 'ESG & Carbon Management',
      icon: Leaf,
      color: 'bg-emerald-100 text-emerald-700',
      features: [
        'Carbon emissions tracking (Scope 1, 2, 3)',
        'Renewable Energy Certificate (REC) inventory',
        'REC retirement workflow',
        'Carbon reduction target tracking',
        'Science-Based Targets (SBTi) support',
        'ESG report generation (CDP, GRI, TCFD, CSRD)',
        'Carbon intensity benchmarking'
      ]
    },
    {
      title: 'Load Forecasting',
      icon: Activity,
      color: 'bg-cyan-100 text-cyan-700',
      features: [
        'ML-based consumption forecasting (Prophet + XGBoost)',
        'Consumption heatmap (hour × day of week)',
        'Anomaly detection (spikes, drops, pattern changes)',
        'Budget tracking and variance analysis',
        'Weather impact correlation analysis',
        'Location-based forecast accuracy tracking',
        'Forecast confidence intervals'
      ]
    },
    {
      title: 'Queue Management',
      icon: ListTodo,
      color: 'bg-yellow-100 text-yellow-700',
      features: [
        'Priority-scored contract work queue',
        'Claim and release workflow',
        'Queue item status tracking',
        'User assignment management',
        'Priority tier visualization',
        'Action tracking and notes'
      ]
    },
    {
      title: 'Market Intelligence',
      icon: TrendingUp,
      color: 'bg-red-100 text-red-700',
      features: [
        'Regional market rate tracking',
        'ISO territory rate comparisons',
        'Rate trend analysis',
        'Product type rate breakdowns',
        'Term-based rate comparisons'
      ]
    }
  ];

  const architectureComponents = [
    { name: 'React Frontend', type: 'frontend', description: 'Modern SPA with TanStack Query, Recharts, Leaflet maps' },
    { name: 'FastAPI Backend', type: 'backend', description: 'High-performance Python API with async MongoDB' },
    { name: 'MongoDB', type: 'database', description: 'Document database for flexible contract data storage' },
    { name: 'OpenAI GPT', type: 'ai', description: 'AI-powered rate analysis and predictions' },
    { name: 'Salesforce CRM', type: 'external', description: 'Contract and account data synchronization' },
  ];

  return (
    <div className="p-8 space-y-8" data-testid="about-page">
      {/* Header with Logo */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <span className="text-gradient-primary">About PolarisIQ</span>
          </h1>
          <p className="text-muted-foreground mt-2 text-lg">
            Navigate Every Renewal - Commercial Energy Contract Intelligence Platform
          </p>
        </div>
        <div className="flex items-center gap-4">
          <a href="https://www.junolabs.ai" target="_blank" rel="noopener noreferrer">
            <img 
              src="https://customer-assets.emergentagent.com/job_energy-forecast-10/artifacts/i572pk3n_Junolabsai%20Logo.png"
              alt="Junolabs.AI"
              className="h-16 w-auto hover:opacity-80 transition-opacity"
            />
          </a>
        </div>
      </div>

      {/* Overview */}
      <Card className="bg-gradient-to-br from-blue-50 to-indigo-100 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-6">
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">Platform Overview</h2>
              <p className="text-blue-800 leading-relaxed">
                PolarisIQ is an enterprise-grade commercial energy contract management platform that combines 
                AI-powered analytics, predictive modeling, and real-time market intelligence to help energy 
                providers optimize their renewal processes, reduce churn, and maximize portfolio value.
              </p>
              <div className="flex flex-wrap gap-2 mt-4">
                <Badge className="bg-blue-200 text-blue-800">AI-Powered</Badge>
                <Badge className="bg-blue-200 text-blue-800">Real-time Analytics</Badge>
                <Badge className="bg-blue-200 text-blue-800">Predictive Modeling</Badge>
                <Badge className="bg-blue-200 text-blue-800">Salesforce Integration</Badge>
                <Badge className="bg-blue-200 text-blue-800">ESG Compliant</Badge>
              </div>
            </div>
            <div className="hidden md:block">
              <div className="w-32 h-32 rounded-full bg-gradient-to-br from-[#09409C] to-[#146CBB] flex items-center justify-center">
                <Globe className="w-16 h-16 text-white" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Features Grid */}
      <div>
        <h2 className="text-2xl font-bold mb-6">Platform Features</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {featureCategories.map((category, idx) => {
            const Icon = category.icon;
            return (
              <Card key={idx} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${category.color}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <CardTitle className="text-lg">{category.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {category.features.map((feature, fidx) => (
                      <li key={fidx} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Architecture Section */}
      <div>
        <h2 className="text-2xl font-bold mb-6">System Architecture</h2>
        <Card>
          <CardContent className="pt-6">
            {/* Architecture Diagram */}
            <div className="relative bg-gray-50 rounded-xl p-8 mb-6">
              <div className="flex flex-col items-center gap-8">
                {/* External Systems Row */}
                <div className="flex items-center justify-center gap-8 w-full">
                  <div className="flex flex-col items-center">
                    <div className="w-24 h-24 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                      <Cloud className="w-12 h-12 text-white" />
                    </div>
                    <span className="mt-2 font-semibold text-sm">Salesforce CRM</span>
                    <span className="text-xs text-muted-foreground">Contract Data Source</span>
                  </div>
                  
                  <div className="flex items-center">
                    <div className="w-16 h-0.5 bg-blue-300"></div>
                    <ArrowRight className="w-6 h-6 text-blue-400" />
                  </div>
                  
                  <div className="flex flex-col items-center">
                    <div className="w-24 h-24 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg">
                      <Brain className="w-12 h-12 text-white" />
                    </div>
                    <span className="mt-2 font-semibold text-sm">OpenAI GPT</span>
                    <span className="text-xs text-muted-foreground">AI Analytics Engine</span>
                  </div>
                </div>

                {/* Connector Lines */}
                <div className="flex items-center gap-4">
                  <div className="w-0.5 h-8 bg-gray-300"></div>
                  <div className="w-0.5 h-8 bg-gray-300 ml-48"></div>
                </div>

                {/* Core Platform Row */}
                <div className="flex items-center justify-center gap-6">
                  <div className="flex flex-col items-center">
                    <div className="w-32 h-32 rounded-xl bg-gradient-to-br from-[#09409C] to-[#146CBB] flex items-center justify-center shadow-xl">
                      <Server className="w-16 h-16 text-white" />
                    </div>
                    <span className="mt-2 font-bold">FastAPI Backend</span>
                    <span className="text-xs text-muted-foreground">API & Business Logic</span>
                  </div>
                  
                  <div className="flex items-center">
                    <div className="w-8 h-0.5 bg-gray-300"></div>
                    <ArrowRight className="w-5 h-5 text-gray-400" />
                    <ArrowRight className="w-5 h-5 text-gray-400 -ml-3 rotate-180" />
                    <div className="w-8 h-0.5 bg-gray-300"></div>
                  </div>
                  
                  <div className="flex flex-col items-center">
                    <div className="w-32 h-32 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-xl">
                      <Database className="w-16 h-16 text-white" />
                    </div>
                    <span className="mt-2 font-bold">MongoDB</span>
                    <span className="text-xs text-muted-foreground">Data Storage</span>
                  </div>
                </div>

                {/* Connector to Frontend */}
                <div className="w-0.5 h-8 bg-gray-300"></div>

                {/* Frontend Row */}
                <div className="flex flex-col items-center">
                  <div className="w-48 h-24 rounded-xl bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center shadow-xl">
                    <div className="text-center text-white">
                      <LayoutDashboard className="w-10 h-10 mx-auto" />
                      <span className="text-sm font-semibold">React Frontend</span>
                    </div>
                  </div>
                  <span className="mt-2 text-xs text-muted-foreground">User Interface</span>
                </div>

                {/* Users */}
                <div className="flex items-center gap-4 mt-4">
                  <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow border">
                    <Users className="w-4 h-4 text-gray-600" />
                    <span className="text-sm">Account Managers</span>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow border">
                    <BarChart3 className="w-4 h-4 text-gray-600" />
                    <span className="text-sm">Executives</span>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow border">
                    <Shield className="w-4 h-4 text-gray-600" />
                    <span className="text-sm">Administrators</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Component Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {architectureComponents.map((component, idx) => (
                <div 
                  key={idx}
                  className={`p-4 rounded-lg border ${
                    component.type === 'frontend' ? 'bg-cyan-50 border-cyan-200' :
                    component.type === 'backend' ? 'bg-blue-50 border-blue-200' :
                    component.type === 'database' ? 'bg-green-50 border-green-200' :
                    component.type === 'ai' ? 'bg-purple-50 border-purple-200' :
                    'bg-gray-50 border-gray-200'
                  }`}
                >
                  <h4 className="font-semibold">{component.name}</h4>
                  <p className="text-sm text-muted-foreground mt-1">{component.description}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Data Flow */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5" />
            Salesforce Integration Flow
          </CardTitle>
          <CardDescription>How contract data flows through the system</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <Cloud className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="font-semibold">Salesforce</p>
                <p className="text-xs text-muted-foreground">Source of Truth</p>
              </div>
            </div>
            
            <ArrowRight className="w-6 h-6 text-gray-400" />
            
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center">
                <RefreshCw className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="font-semibold">Sync Service</p>
                <p className="text-xs text-muted-foreground">Bi-directional Sync</p>
              </div>
            </div>
            
            <ArrowRight className="w-6 h-6 text-gray-400" />
            
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                <Database className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="font-semibold">MongoDB</p>
                <p className="text-xs text-muted-foreground">Local Cache</p>
              </div>
            </div>
            
            <ArrowRight className="w-6 h-6 text-gray-400" />
            
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                <Brain className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="font-semibold">AI Analysis</p>
                <p className="text-xs text-muted-foreground">Rate & Predictions</p>
              </div>
            </div>
            
            <ArrowRight className="w-6 h-6 text-gray-400" />
            
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-cyan-100 flex items-center justify-center">
                <LayoutDashboard className="w-6 h-6 text-cyan-600" />
              </div>
              <div>
                <p className="font-semibold">Dashboard</p>
                <p className="text-xs text-muted-foreground">User Interface</p>
              </div>
            </div>
          </div>
          
          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start gap-3">
              <Lock className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div>
                <p className="font-semibold text-yellow-800">Integration Status</p>
                <p className="text-sm text-yellow-700">
                  Salesforce integration is currently using <strong>mocked data</strong> for demonstration purposes. 
                  Production deployment will enable live bi-directional synchronization with your Salesforce CRM instance.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tech Stack */}
      <Card>
        <CardHeader>
          <CardTitle>Technology Stack</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="font-bold text-lg">React 18</p>
              <p className="text-sm text-muted-foreground">Frontend Framework</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="font-bold text-lg">FastAPI</p>
              <p className="text-sm text-muted-foreground">Backend API</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="font-bold text-lg">MongoDB</p>
              <p className="text-sm text-muted-foreground">Database</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="font-bold text-lg">TailwindCSS</p>
              <p className="text-sm text-muted-foreground">Styling</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="font-bold text-lg">Recharts</p>
              <p className="text-sm text-muted-foreground">Data Visualization</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="font-bold text-lg">Leaflet</p>
              <p className="text-sm text-muted-foreground">Maps & Geospatial</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="font-bold text-lg">OpenAI</p>
              <p className="text-sm text-muted-foreground">AI/ML Engine</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="font-bold text-lg">Salesforce</p>
              <p className="text-sm text-muted-foreground">CRM Integration</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Footer with Junolabs Logo */}
      <div className="flex items-center justify-center pt-8 border-t">
        <div className="text-center">
          <p className="text-sm text-muted-foreground mb-4">Developed by</p>
          <a href="https://www.junolabs.ai" target="_blank" rel="noopener noreferrer">
            <img 
              src="https://customer-assets.emergentagent.com/job_energy-forecast-10/artifacts/i572pk3n_Junolabsai%20Logo.png"
              alt="Junolabs.AI"
              className="h-12 w-auto mx-auto hover:opacity-80 transition-opacity"
            />
          </a>
        </div>
      </div>
    </div>
  );
}
